import { useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import type { VisibleNavItem, VisibleSidebarSection } from '@portal/platform/configuration';
import { useSession } from '@portal/platform/session';
import { AppLink } from '../app-link';
import { fill } from '../i18n/shell-texts';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Icon } from '../kit/icon';
import { activeSectionId, isActive } from './active-route';
import { navIconMarkup } from './nav-icons';
import { usePinnedItems } from './nav-preferences';
import { OrgNodeSelector } from './org-node-selector';

/** Waiting-item counts per menu entry id; nothing is drawn for 0 or a missing count. */
export type NavBadges = Readonly<Record<string, number | null | undefined>>;

function NavIcon({ name }: { name: string | undefined }) {
  // Static markup from nav-icons.ts, never response data.
  return (
    <svg
      className="h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      viewBox="0 0 24 24"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: navIconMarkup(name) }}
    />
  );
}

/** Sentence case, a quiet tint and a brand bar on the current entry. */
const itemClass =
  'relative flex min-w-0 flex-1 items-center gap-2.5 rounded-bs px-2.5 py-1.5 text-[13.5px] text-ink no-underline transition-colors hover:bg-cap focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-input-focus aria-[current=page]:bg-brand/10 aria-[current=page]:font-semibold aria-[current=page]:text-brand-dark aria-[current=page]:shadow-[inset_3px_0_0_var(--color-brand)]';

/** In the folded menu (desktop only), an entry is its icon, centred. */
const railItemClass = 'lg:justify-center lg:px-0 lg:py-2';

const sectionTitleClass = 'px-2.5 text-[11px] font-bold tracking-wider text-ink-muted uppercase';

interface EntryProps {
  item: VisibleNavItem;
  pathname: string;
  collapsed: boolean;
  badge: number | null | undefined;
  /** The entry's section, named beside it where it is shown out of it (the pinned list). */
  section?: string;
  pinned: boolean;
  onTogglePin: (id: string) => void;
  onNavigate: () => void;
}

function NavEntry({
  item,
  pathname,
  collapsed,
  badge,
  section,
  pinned,
  onTogglePin,
  onNavigate,
}: EntryProps) {
  const t = useShellTexts().sidebar;
  // Out of its section, "Kunder" alone does not say whose customers.
  const fullLabel = section ? fill(t.inSection, { label: item.label, section }) : item.label;
  const children = (item.children ?? []).filter(
    child => child.enabled !== false && child.visible !== false,
  );
  const [open, setOpen] = useState(() => children.some(child => isActive(pathname, child.route)));
  const count = badge && badge > 0 ? badge : null;
  const pinnable = Boolean(item.route || item.externalUrl);
  // Room on the right for the pin, which sits over the entry so the highlight spans the row.
  const className = `${itemClass} ${pinnable ? 'pr-8' : ''} ${collapsed ? `${railItemClass} lg:pr-0` : ''}`;
  const label = (
    <>
      <NavIcon name={item.icon} />
      <span className={`truncate ${collapsed ? 'lg:sr-only' : ''}`}>
        {item.label}
        {section ? (
          <span className="ml-1.5 text-xs font-normal text-ink-muted">{section}</span>
        ) : null}
      </span>
      {count !== null ? (
        <span
          aria-hidden="true"
          className={`ml-auto inline-grid h-[18px] min-w-[18px] place-items-center rounded-full bg-danger px-1.5 text-[11px] font-bold text-white ${collapsed ? 'lg:absolute lg:top-0 lg:right-1 lg:ml-0' : ''}`}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </>
  );
  // The badge is drawn for the eye; the link's name says it in words.
  const name =
    count !== null
      ? fill(t.badge, { label: fullLabel, count: String(count) })
      : section
        ? fullLabel
        : undefined;

  const link = item.externalUrl ? (
    <a
      href={item.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={fullLabel}
      className={className}
      aria-label={`${fullLabel} (${t.opensNewTab})`}
    >
      {label}
    </a>
  ) : item.route ? (
    <AppLink
      href={item.route}
      title={collapsed ? fullLabel : undefined}
      aria-label={name}
      aria-current={isActive(pathname, item.route) ? 'page' : undefined}
      className={className}
      onClick={onNavigate}
    >
      {label}
    </AppLink>
  ) : (
    <span className={className}>{label}</span>
  );

  return (
    <li className="group/entry">
      <div className="relative flex items-center">
        {link}
        {pinnable ? (
          <button
            type="button"
            aria-pressed={pinned}
            aria-label={fill(pinned ? t.unpin : t.pin, { label: fullLabel })}
            title={fill(pinned ? t.unpin : t.pin, { label: fullLabel })}
            onClick={() => onTogglePin(item.id)}
            className={`absolute top-1/2 right-1 -translate-y-1/2 rounded-bs p-1 text-ink-muted hover:bg-cap hover:text-ink focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-input-focus ${pinned ? 'opacity-100' : 'opacity-0 group-hover/entry:opacity-100'} ${collapsed ? 'lg:hidden' : ''}`}
          >
            <PinGlyph filled={pinned} />
          </button>
        ) : null}
        {children.length > 0 ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={item.label}
            onClick={() => setOpen(value => !value)}
            className={`rounded-bs px-2 py-1.5 text-ink-muted hover:bg-cap hover:text-ink ${collapsed ? 'lg:hidden' : ''}`}
          >
            <Icon name={open ? 'caret-down' : 'caret-right'} />
          </button>
        ) : null}
      </div>
      {open && children.length > 0 ? (
        <ul className={collapsed ? 'lg:hidden' : undefined}>
          {children.map(child => (
            <li key={child.id}>
              {child.route ? (
                <AppLink
                  href={child.route}
                  aria-current={isActive(pathname, child.route) ? 'page' : undefined}
                  onClick={onNavigate}
                  className={`${itemClass} pl-9`}
                >
                  {child.label}
                </AppLink>
              ) : (
                <span className={`${itemClass} pl-9 text-ink-muted`}>{child.label}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function PinGlyph({ filled }: { filled: boolean }) {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.75}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 3.75 20.25 7.5l-3 1.5-3.75 3.75.75 4.5-1.5 1.5-3.75-3.75L4.5 19.5M9 9.75 5.25 13.5l5.25 5.25M15 4.5l-3 3"
      />
    </svg>
  );
}

function SectionChevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );
}

/**
 * The backend layout's sections and items (navigation design A, LEGACY_PARITY_RUNS.md P7):
 *
 * - the org node the portal is scoped to, first, as a card;
 * - the entries the user pinned (by default the app's work lists), with waiting-item counts;
 * - the sections. A section with an overview page (`route`) is a link to it, and its entries
 *   show exactly while the page is the overview or one of the section's own pages. A section
 *   without one folds open from its heading, one at a time, and opens by itself on its pages;
 * - at the bottom, a switch that folds the menu to an icon rail (desktop), remembered per browser.
 *
 * Below `lg` it is an off-canvas panel over a backdrop, always unfolded, with a logout entry at
 * the bottom since the header's user menu is hidden there.
 */
export function Sidebar({
  sections,
  collapsed,
  onCollapsedChange,
  mobileOpen = false,
  onMobileClose,
  logoutLabel,
  onLogout,
  defaultPins = [],
  badges = {},
}: {
  sections: VisibleSidebarSection[];
  /** Folded to the icon rail (desktop). */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /** Shown below `lg` (the header's ☰ there). */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** The mobile logout entry; none without both. */
  logoutLabel?: string;
  onLogout?: () => void;
  /** Entry ids pinned until the user changes them. */
  defaultPins?: readonly string[];
  badges?: NavBadges;
}) {
  const t = useShellTexts().sidebar;
  const { pathname } = useLocation();
  const { identity } = useSession();
  const { pinned, toggle } = usePinnedItems(identity?.userId ?? null, defaultPins);
  const closeMobile = () => onMobileClose?.();

  // The page's own section. A section with an overview page is open exactly while it is this;
  // the others fold by hand, one at a time, and arriving on one of their pages (from a pin, a
  // link, the address bar) opens it.
  const active = activeSectionId(sections, pathname);
  const [openId, setOpenId] = useState<string | null>(active);
  const [seenActive, setSeenActive] = useState(active);
  if (active !== seenActive) {
    setSeenActive(active);
    if (active) {
      setOpenId(active);
    }
  }

  const pinnedItems = pinned.flatMap(id => {
    const section = sections.find(candidate => candidate.items.some(item => item.id === id));
    const item = section?.items.find(candidate => candidate.id === id);
    return section && item ? [{ item, section: section.label || undefined }] : [];
  });

  const entry = (item: VisibleNavItem, section?: string) => (
    <NavEntry
      key={item.id}
      item={item}
      pathname={pathname}
      collapsed={collapsed}
      badge={badges[item.id]}
      section={section}
      pinned={pinned.includes(item.id)}
      onTogglePin={toggle}
      onNavigate={closeMobile}
    />
  );

  return (
    <>
      {mobileOpen ? (
        <div
          aria-hidden="true"
          className="fixed inset-0 top-header z-[290] bg-black/50 lg:hidden"
          onClick={closeMobile}
        />
      ) : null}
      <nav
        aria-label={t.navigation}
        className={`fixed top-header left-0 z-[300] flex h-[calc(100dvh-var(--spacing-header))] w-sidebar flex-col border-r border-line bg-white transition-[translate,visibility,width] duration-[250ms] ${mobileOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'} lg:visible lg:translate-x-0 ${collapsed ? 'lg:w-rail' : ''}`}
      >
        <div className={`shrink-0 p-3 ${collapsed ? 'lg:flex lg:justify-center lg:px-0' : ''}`}>
          <div className={collapsed ? 'lg:hidden' : undefined}>
            <OrgNodeSelector />
          </div>
          {collapsed ? (
            <div className="hidden lg:block">
              <OrgNodeSelector variant="rail" />
            </div>
          ) : null}
        </div>
        <div className={`min-h-0 flex-1 overflow-y-auto px-3 pb-3 ${collapsed ? 'lg:px-2' : ''}`}>
          {pinnedItems.length > 0 ? (
            <div role="group" aria-label={t.pinned} className="mb-2">
              <div className={`${sectionTitleClass} pt-1 pb-1 ${collapsed ? 'lg:hidden' : ''}`}>
                {t.pinned}
              </div>
              <ul>{pinnedItems.map(pin => entry(pin.item, pin.section))}</ul>
              {collapsed ? <hr className="my-2 hidden border-line lg:block" /> : null}
            </div>
          ) : null}
          <ul>
            {sections.map((section, index) => {
              if (section.flat) {
                return (
                  <li key={section.id} role="group" aria-label={section.label || undefined}>
                    {index > 0 ? <hr className="my-2 border-line" /> : null}
                    <ul>{section.items.map(item => entry(item))}</ul>
                  </li>
                );
              }
              const open = section.route ? active === section.id : openId === section.id;
              const listId = `nav-section-${section.id}`;
              return (
                <li key={section.id} role="group" aria-label={section.label || undefined}>
                  {section.route ? (
                    // A section with an overview page: the heading opens it, which is also what
                    // shows the section's entries. The chevron only tells which way it is.
                    <AppLink
                      href={section.route}
                      aria-current={isActive(pathname, section.route) ? 'page' : undefined}
                      onClick={closeMobile}
                      className={`${sectionTitleClass} flex w-full items-center justify-between rounded-bs py-2 no-underline hover:text-ink focus-visible:outline-2 focus-visible:outline-input-focus aria-[current=page]:text-brand-dark ${collapsed ? 'lg:hidden' : ''}`}
                    >
                      {section.label}
                      <SectionChevron open={open} />
                    </AppLink>
                  ) : (
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={listId}
                      onClick={() => setOpenId(open ? null : section.id)}
                      className={`${sectionTitleClass} flex w-full items-center justify-between rounded-bs py-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-input-focus ${collapsed ? 'lg:hidden' : ''}`}
                    >
                      {section.label}
                      <SectionChevron open={open} />
                    </button>
                  )}
                  {collapsed && index > 0 ? (
                    <hr className="my-2 hidden border-line lg:block" />
                  ) : null}
                  {/* The rail shows every entry: an icon list has no room for headings to open. */}
                  <ul
                    id={listId}
                    className={`pb-1 ${open ? '' : collapsed ? 'hidden lg:block' : 'hidden'}`}
                  >
                    {section.items.map(item => entry(item))}
                  </ul>
                </li>
              );
            })}
            {logoutLabel && onLogout ? (
              <li className="mt-2 lg:hidden">
                <button
                  type="button"
                  className={`${itemClass} w-full`}
                  onClick={() => {
                    closeMobile();
                    onLogout();
                  }}
                >
                  <Icon name="lock" className="w-4" />
                  <span>{logoutLabel}</span>
                </button>
              </li>
            ) : null}
          </ul>
        </div>
        <div className="hidden shrink-0 border-t border-line p-2 lg:block">
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-label={collapsed ? t.show : t.hide}
            title={collapsed ? t.show : t.hide}
            onClick={() => onCollapsedChange(!collapsed)}
            className={`flex w-full items-center gap-2 rounded-bs px-2.5 py-1.5 text-xs text-ink-muted hover:bg-cap hover:text-ink focus-visible:outline-2 focus-visible:outline-input-focus ${collapsed ? 'justify-center px-0' : ''}`}
          >
            <svg
              className={`h-4 w-4 transition-transform ${collapsed ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15L5.25 12l7.5 7.5"
              />
            </svg>
            {collapsed ? null : <span>{t.hide}</span>}
          </button>
        </div>
      </nav>
    </>
  );
}
